import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';

// --- Types ---

interface SettingsResponse {
  data: Record<string, string>;
}

interface SectionProps {
  queryClient: ReturnType<typeof useQueryClient>;
  addToast: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void;
}

// --- Collapsible Panel Wrapper ---

function SettingsPanel({
  icon,
  title,
  description,
  borderColor,
  defaultOpen,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  borderColor: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={`collapse collapse-arrow bg-base-100 shadow-sm border-l-4 ${borderColor}`}>
      <input type="checkbox" defaultChecked={defaultOpen} />
      <div className="collapse-title">
        <div className="flex items-center gap-3">
          <span className="text-xl">{icon}</span>
          <div>
            <h3 className="font-semibold text-base">{title}</h3>
            <p className="text-xs text-base-content/50">{description}</p>
          </div>
        </div>
      </div>
      <div className="collapse-content">
        <div className="pt-2">{children}</div>
      </div>
    </div>
  );
}

// --- Component ---

export default function SystemSettingsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Configuracion del sistema</h1>
        <p className="text-sm text-base-content/60">Email, almacenamiento, notificaciones y ajustes generales</p>
      </div>

      <div className="flex flex-col gap-4 max-w-3xl">
        <SettingsPanel
          icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
          title="Email (SMTP)"
          description="Configuracion del servidor de correo saliente"
          borderColor="border-primary"
          defaultOpen
        >
          <EmailSettings queryClient={queryClient} addToast={addToast} />
        </SettingsPanel>

        <SettingsPanel
          icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg>}
          title="Almacenamiento"
          description="Archivos locales o en la nube (S3, MinIO, DigitalOcean Spaces)"
          borderColor="border-secondary"
        >
          <StorageSettings queryClient={queryClient} addToast={addToast} />
        </SettingsPanel>

        <SettingsPanel
          icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>}
          title="Notificaciones"
          description="Push, email y tipos de notificaciones del sistema"
          borderColor="border-accent"
        >
          <NotificationSettings queryClient={queryClient} addToast={addToast} />
        </SettingsPanel>

        <SettingsPanel
          icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
          title="General"
          description="Nombre del sitio, idioma, modo mantenimiento"
          borderColor="border-info"
        >
          <GeneralSettings queryClient={queryClient} addToast={addToast} />
        </SettingsPanel>
      </div>
    </div>
  );
}

// --- Email Settings ---

function EmailSettings({ queryClient, addToast }: SectionProps): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings-email'],
    queryFn: () => api.get<SettingsResponse>('/v1/admin/settings/email'),
  });

  const [form, setForm] = useState({
    email_provider: 'console',
    email_smtp_host: '',
    email_smtp_port: '587',
    email_smtp_user: '',
    email_smtp_password: '',
    email_smtp_tls: 'true',
    email_from_address: '',
    email_from_name: 'SGI',
  });

  useEffect(() => {
    if (data?.data) {
      setForm((prev) => ({ ...prev, ...data.data }));
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: typeof form) => api.put('/v1/admin/settings/email', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings-email'] });
      addToast('Configuracion de email guardada', 'success');
    },
  });

  const testMutation = useMutation({
    mutationFn: () => api.post('/v1/admin/settings/email/test'),
    onSuccess: () => addToast('Email de prueba enviado', 'success'),
  });

  const handleSave = (e: React.FormEvent): void => {
    e.preventDefault();
    saveMutation.mutate(form);
  };

  const set = (key: string, value: string): void => setForm((prev) => ({ ...prev, [key]: value }));

  if (isLoading) return <div className="flex justify-center p-8"><span className="loading loading-spinner loading-lg" /></div>;

  const isSmtp = form.email_provider === 'smtp';

  return (
    <form onSubmit={handleSave}>
      {/* Provider */}
      <div className="form-control w-full mb-4">
        <label className="label"><span className="label-text font-medium">Proveedor de email</span></label>
        <select className="select select-bordered w-full" value={form.email_provider} onChange={(e) => set('email_provider', e.target.value)}>
          <option value="console">Consola (desarrollo)</option>
          <option value="smtp">SMTP</option>
        </select>
        <label className="label">
          <span className="label-text-alt text-base-content/50">
            {isSmtp ? 'Los emails se enviaran via SMTP' : 'Los emails se muestran en la consola del servidor'}
          </span>
        </label>
      </div>

      {isSmtp && (
        <>
          <div className="divider text-sm text-base-content/40">Configuracion SMTP</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label"><span className="label-text">Servidor SMTP *</span></label>
              <input className="input input-bordered" placeholder="smtp.gmail.com" value={form.email_smtp_host} onChange={(e) => set('email_smtp_host', e.target.value)} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Puerto *</span></label>
              <input className="input input-bordered" placeholder="587" value={form.email_smtp_port} onChange={(e) => set('email_smtp_port', e.target.value)} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Usuario SMTP</span></label>
              <input className="input input-bordered" placeholder="user@gmail.com" value={form.email_smtp_user} onChange={(e) => set('email_smtp_user', e.target.value)} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Contrasena SMTP</span></label>
              <input className="input input-bordered" type="password" placeholder="••••••••" value={form.email_smtp_password} onChange={(e) => set('email_smtp_password', e.target.value)} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Email remitente</span></label>
              <input className="input input-bordered" placeholder="noreply@sgi.com" value={form.email_from_address} onChange={(e) => set('email_from_address', e.target.value)} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Nombre remitente</span></label>
              <input className="input input-bordered" placeholder="SGI" value={form.email_from_name} onChange={(e) => set('email_from_name', e.target.value)} />
            </div>
          </div>

          <div className="form-control mt-4">
            <label className="label cursor-pointer justify-start gap-3">
              <input type="checkbox" className="toggle toggle-primary" checked={form.email_smtp_tls === 'true'} onChange={(e) => set('email_smtp_tls', e.target.checked ? 'true' : 'false')} />
              <span className="label-text">Usar TLS (recomendado)</span>
            </label>
          </div>
        </>
      )}

      <div className="flex gap-2 justify-end mt-6 pt-4 border-t border-base-200">
        {isSmtp && (
          <button type="button" className="btn btn-outline btn-sm" onClick={() => testMutation.mutate()} disabled={testMutation.isPending}>
            {testMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : 'Enviar email de prueba'}
          </button>
        )}
        <button type="submit" className="btn btn-primary btn-sm" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

// --- Storage Settings ---

function StorageSettings({ queryClient, addToast }: SectionProps): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings-storage'],
    queryFn: () => api.get<SettingsResponse>('/v1/admin/settings/storage'),
  });

  const [form, setForm] = useState({
    storage_provider: 'local',
    storage_s3_bucket: '',
    storage_s3_region: 'us-east-1',
    storage_s3_access_key: '',
    storage_s3_secret_key: '',
    storage_s3_endpoint: '',
    storage_s3_cdn_url: '',
  });

  useEffect(() => {
    if (data?.data) {
      setForm((prev) => ({ ...prev, ...data.data }));
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: typeof form) => api.put('/v1/admin/settings/storage', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings-storage'] });
      addToast('Configuracion de almacenamiento guardada', 'success');
    },
  });

  const handleSave = (e: React.FormEvent): void => {
    e.preventDefault();
    saveMutation.mutate(form);
  };

  const set = (key: string, value: string): void => setForm((prev) => ({ ...prev, [key]: value }));

  if (isLoading) return <div className="flex justify-center p-8"><span className="loading loading-spinner loading-lg" /></div>;

  const isS3 = form.storage_provider === 's3';

  return (
    <form onSubmit={handleSave}>
      {/* Provider */}
      <div className="form-control w-full mb-4">
        <label className="label"><span className="label-text font-medium">Proveedor de almacenamiento</span></label>
        <select className="select select-bordered w-full" value={form.storage_provider} onChange={(e) => set('storage_provider', e.target.value)}>
          <option value="local">Local (desarrollo)</option>
          <option value="s3">Amazon S3 / Compatible</option>
        </select>
        <label className="label">
          <span className="label-text-alt text-base-content/50">
            {isS3 ? 'Los archivos se suben a S3 o storage compatible (MinIO, DigitalOcean Spaces, etc.)' : 'Los archivos se guardan en el servidor local (se pierden en deploy)'}
          </span>
        </label>
      </div>

      {isS3 && (
        <>
          <div className="divider text-sm text-base-content/40">Configuracion S3</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label"><span className="label-text">Bucket *</span></label>
              <input className="input input-bordered" placeholder="sgi-uploads" value={form.storage_s3_bucket} onChange={(e) => set('storage_s3_bucket', e.target.value)} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Region</span></label>
              <input className="input input-bordered" placeholder="us-east-1" value={form.storage_s3_region} onChange={(e) => set('storage_s3_region', e.target.value)} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Access Key *</span></label>
              <input className="input input-bordered" placeholder="AKIA..." value={form.storage_s3_access_key} onChange={(e) => set('storage_s3_access_key', e.target.value)} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Secret Key *</span></label>
              <input className="input input-bordered" type="password" placeholder="••••••••" value={form.storage_s3_secret_key} onChange={(e) => set('storage_s3_secret_key', e.target.value)} />
            </div>
            <div className="form-control sm:col-span-2">
              <label className="label"><span className="label-text">Endpoint personalizado</span></label>
              <input className="input input-bordered" placeholder="https://s3.us-east-1.amazonaws.com (dejar vacio para AWS)" value={form.storage_s3_endpoint} onChange={(e) => set('storage_s3_endpoint', e.target.value)} />
              <label className="label"><span className="label-text-alt text-base-content/50">Para MinIO, DigitalOcean Spaces u otros compatibles con S3</span></label>
            </div>
            <div className="form-control sm:col-span-2">
              <label className="label"><span className="label-text">CDN URL</span></label>
              <input className="input input-bordered" placeholder="https://cdn.sgi.com (opcional)" value={form.storage_s3_cdn_url} onChange={(e) => set('storage_s3_cdn_url', e.target.value)} />
              <label className="label"><span className="label-text-alt text-base-content/50">URL publica para servir archivos (CloudFront, etc.)</span></label>
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end mt-6 pt-4 border-t border-base-200">
        <button type="submit" className="btn btn-primary btn-sm" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

// --- Notification Settings ---

const NOTIFICATION_TYPES = [
  { key: 'new_message', label: 'Nuevo mensaje', description: 'Cuando un inquilino o propietario envia un mensaje' },
  { key: 'visit_reminder', label: 'Recordatorio de visita', description: 'Antes de una visita programada' },
  { key: 'contract_expiry', label: 'Vencimiento de contrato', description: 'Cuando un contrato esta por vencer' },
  { key: 'payment_due', label: 'Pago pendiente', description: 'Cuando un pago esta proximo o vencido' },
] as const;

function NotificationSettings({ queryClient, addToast }: SectionProps): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings-notifications'],
    queryFn: () => api.get<SettingsResponse>('/v1/admin/settings/notifications'),
  });

  const [form, setForm] = useState({
    notif_push_enabled: 'false',
    notif_email_enabled: 'true',
    notif_new_message_email: 'true',
    notif_new_message_push: 'true',
    notif_visit_reminder_email: 'true',
    notif_visit_reminder_push: 'true',
    notif_contract_expiry_email: 'true',
    notif_contract_expiry_push: 'false',
    notif_payment_due_email: 'true',
    notif_payment_due_push: 'false',
  });

  useEffect(() => {
    if (data?.data) {
      setForm((prev) => ({ ...prev, ...data.data }));
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: typeof form) => api.put('/v1/admin/settings/notifications', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings-notifications'] });
      addToast('Configuracion de notificaciones guardada', 'success');
    },
  });

  const handleSave = (e: React.FormEvent): void => {
    e.preventDefault();
    saveMutation.mutate(form);
  };

  const toggle = (key: string): void =>
    setForm((prev) => ({ ...prev, [key]: prev[key as keyof typeof prev] === 'true' ? 'false' : 'true' }));

  if (isLoading) return <div className="flex justify-center p-8"><span className="loading loading-spinner loading-lg" /></div>;

  return (
    <form onSubmit={handleSave}>
      {/* Global toggles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div className="form-control bg-base-200/50 rounded-lg p-4">
          <label className="label cursor-pointer justify-between">
            <div>
              <span className="label-text font-medium">Push en navegador</span>
              <p className="text-xs text-base-content/50 mt-1">Notificaciones push en el navegador web</p>
            </div>
            <input
              type="checkbox"
              className="toggle toggle-accent"
              checked={form.notif_push_enabled === 'true'}
              onChange={() => toggle('notif_push_enabled')}
            />
          </label>
        </div>
        <div className="form-control bg-base-200/50 rounded-lg p-4">
          <label className="label cursor-pointer justify-between">
            <div>
              <span className="label-text font-medium">Email</span>
              <p className="text-xs text-base-content/50 mt-1">Enviar notificaciones por correo electronico</p>
            </div>
            <input
              type="checkbox"
              className="toggle toggle-accent"
              checked={form.notif_email_enabled === 'true'}
              onChange={() => toggle('notif_email_enabled')}
            />
          </label>
        </div>
      </div>

      <div className="divider text-sm text-base-content/40">Tipos de notificacion</div>

      {/* Per-type toggles */}
      <div className="overflow-x-auto">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Tipo</th>
              <th className="text-center w-24">Email</th>
              <th className="text-center w-24">Push</th>
            </tr>
          </thead>
          <tbody>
            {NOTIFICATION_TYPES.map((nt) => (
              <tr key={nt.key}>
                <td>
                  <div>
                    <span className="font-medium text-sm">{nt.label}</span>
                    <p className="text-xs text-base-content/50">{nt.description}</p>
                  </div>
                </td>
                <td className="text-center">
                  <input
                    type="checkbox"
                    className="toggle toggle-sm toggle-primary"
                    checked={form[`notif_${nt.key}_email` as keyof typeof form] === 'true'}
                    onChange={() => toggle(`notif_${nt.key}_email`)}
                    disabled={form.notif_email_enabled !== 'true'}
                  />
                </td>
                <td className="text-center">
                  <input
                    type="checkbox"
                    className="toggle toggle-sm toggle-primary"
                    checked={form[`notif_${nt.key}_push` as keyof typeof form] === 'true'}
                    onChange={() => toggle(`notif_${nt.key}_push`)}
                    disabled={form.notif_push_enabled !== 'true'}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end mt-6 pt-4 border-t border-base-200">
        <button type="submit" className="btn btn-primary btn-sm" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

// --- General Settings ---

function GeneralSettings({ queryClient, addToast }: SectionProps): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings-general'],
    queryFn: () => api.get<SettingsResponse>('/v1/admin/settings/general'),
  });

  const [form, setForm] = useState({
    site_name: 'SGI',
    site_url: '',
    default_language: 'es',
    maintenance_mode: 'false',
  });

  useEffect(() => {
    if (data?.data) {
      setForm((prev) => ({ ...prev, ...data.data }));
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: typeof form) => api.put('/v1/admin/settings/general', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings-general'] });
      addToast('Configuracion general guardada', 'success');
    },
  });

  const handleSave = (e: React.FormEvent): void => {
    e.preventDefault();
    saveMutation.mutate(form);
  };

  const set = (key: string, value: string): void => setForm((prev) => ({ ...prev, [key]: value }));

  if (isLoading) return <div className="flex justify-center p-8"><span className="loading loading-spinner loading-lg" /></div>;

  return (
    <form onSubmit={handleSave}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="form-control">
          <label className="label"><span className="label-text font-medium">Nombre del sitio</span></label>
          <input className="input input-bordered" placeholder="SGI" value={form.site_name} onChange={(e) => set('site_name', e.target.value)} />
          <label className="label"><span className="label-text-alt text-base-content/50">Se muestra en el header y en los emails</span></label>
        </div>
        <div className="form-control">
          <label className="label"><span className="label-text font-medium">URL del sitio</span></label>
          <input className="input input-bordered" placeholder="https://sgi.example.com" value={form.site_url} onChange={(e) => set('site_url', e.target.value)} />
          <label className="label"><span className="label-text-alt text-base-content/50">URL base para links en emails y notificaciones</span></label>
        </div>
        <div className="form-control">
          <label className="label"><span className="label-text font-medium">Idioma predeterminado</span></label>
          <select className="select select-bordered w-full" value={form.default_language} onChange={(e) => set('default_language', e.target.value)}>
            <option value="es">Espanol</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      <div className="divider text-sm text-base-content/40">Mantenimiento</div>

      <div className="form-control bg-warning/10 rounded-lg p-4">
        <label className="label cursor-pointer justify-between">
          <div>
            <span className="label-text font-medium">Modo mantenimiento</span>
            <p className="text-xs text-base-content/50 mt-1">
              Cuando esta activo, solo los administradores pueden acceder al sistema.
              Los demas usuarios veran una pagina de mantenimiento.
            </p>
          </div>
          <input
            type="checkbox"
            className="toggle toggle-warning"
            checked={form.maintenance_mode === 'true'}
            onChange={(e) => set('maintenance_mode', e.target.checked ? 'true' : 'false')}
          />
        </label>
      </div>

      <div className="flex justify-end mt-6 pt-4 border-t border-base-200">
        <button type="submit" className="btn btn-primary btn-sm" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : 'Guardar'}
        </button>
      </div>
    </form>
  );
}
